import type { OfferConst, OfferGroupDataDto } from '@api/types.gen'
import type { PipeInterface } from '@render/composables/usePipe'
import { resolveActiveVariant } from '@render/composables/usePaymentEvents'
import {
  PAYMENT_SESSION_KEY,
  getPaymentTrackingInfo,
  type PaymentSessionState,
} from '@render/composables/paymentSessionStore'

const PPOS_KEY = 'var.global.purchasedPpos'
const PPOS_WITH_ALT_KEY = 'var.global.purchasedPposWithAlternates'
// Sequence-aligned with PPOS_KEY: recordPurchase only ever appends to both in
// the same call, so index i of one always describes index i of the other.
const OFFER_TYPES_KEY = 'var.global.purchasedOfferTypes'
// Running total of what the visitor has actually been charged so far — each
// purchase's payments-api `trackingEvent.amount` (see recordPurchase), in the
// currency it charged. Rounded to a whole number on every write — some
// currencies GTM/the data team compare this against have no minor unit at all,
// so a fixed 2-decimal assumption would misrepresent them.
const OFFER_SUM_KEY = 'var.global.purchasedOfferSum'

/**
 * Coerces one price field off an offer variant to a finite number, or null.
 *
 * `apiBasePrice` / `apiTrialPrice` are typed `number` on `OfferConst` but arrive
 * as **strings** (`"13.50"`) — they come straight off a payments-API `decimal`
 * column, and pg returns numeric/decimal as a string to preserve precision. See
 * `finitePrice` in pipeorders.ts (same coercion, same root cause) for the full
 * writeup.
 */
const finitePrice = (value: number | string | undefined | null): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * What the visitor is actually charged for this variant right now: on a
 * `recurring_with_trial` offer that's the trial price (the recurring
 * `apiBasePrice` only bills after the trial ends); every other payment type
 * charges `apiBasePrice` up front. Same `paymentType === 'recurring_with_trial'`
 * branch OfferSelectV2/OfferSelectComponent/pipeorders.ts use. Only the fallback
 * for a purchase whose session carries no payments-api amount.
 */
const chargedPriceOf = (variant: OfferConst): number | null =>
  finitePrice(
    variant.paymentType === 'recurring_with_trial' ? variant.apiTrialPrice : variant.apiBasePrice,
  )

const readList = (pipe: PipeInterface, key: string): string[] => {
  const v = pipe.getData(key)
  return Array.isArray(v) ? (v as unknown[]).filter((x): x is string => typeof x === 'string') : []
}

const appendUnique = (list: string[], items: (string | undefined)[]): string[] => {
  const set = new Set(list)
  for (const it of items) if (it) set.add(it)
  return [...set]
}

export const isPpoPurchased = (pipe: PipeInterface, ppoId: string | undefined): boolean => {
  if (!ppoId) return false
  return readList(pipe, PPOS_WITH_ALT_KEY).includes(ppoId)
}

// Always seeds `chargedPpoId` into the alternates list so single-offer groups
// are blocked too; both `regular.ppoId` and `exit.ppoId` are merged in
// regardless of which variant was charged so the alternate is also blocked.
//
// Logs once per first-time recording. Idempotent reruns (e.g. PrimerCheckout's
// skip-path also calls onCheckoutComplete which routes here) are silent so
// the log faithfully reflects fresh charges only.
export const recordPurchase = (
  pipe: PipeInterface,
  offerGroup: OfferGroupDataDto | null | undefined,
  chargedPpoId: string | undefined,
  pipelineOfferType?: string,
): void => {
  if (!chargedPpoId) return
  const ppos = readList(pipe, PPOS_KEY)
  if (ppos.includes(chargedPpoId)) return

  const reg = offerGroup?.regular?.ppoId
  const exit = offerGroup?.exit?.ppoId
  pipe.setData(PPOS_KEY, appendUnique(ppos, [chargedPpoId]))
  pipe.setData(
    PPOS_WITH_ALT_KEY,
    appendUnique(readList(pipe, PPOS_WITH_ALT_KEY), [chargedPpoId, reg, exit]),
  )

  if (pipelineOfferType) {
    pipe.setData(OFFER_TYPES_KEY, [...readList(pipe, OFFER_TYPES_KEY), pipelineOfferType])
  }

  // What payments-api charged: the trackingEvent of the session every checkout
  // records as completed (paymentSession.completed.current) right before
  // calling here — the amount GTM's `order completed` reports as
  // paymentsApiPrice.
  const sessions = pipe.getData(PAYMENT_SESSION_KEY) as PaymentSessionState | null | undefined
  const isExitIntent = !!pipe.getData('var.global.hasExitIntentBeenAccepted')
  const variant = offerGroup ? resolveActiveVariant(offerGroup, isExitIntent) : undefined
  const chargedAmount =
    getPaymentTrackingInfo(sessions?.completed?.current).paymentsApiPrice ??
    (variant ? chargedPriceOf(variant) : null)
  if (chargedAmount !== null) {
    const prevSum = finitePrice(pipe.getData(OFFER_SUM_KEY) as number | string | undefined) ?? 0
    pipe.setData(OFFER_SUM_KEY, Math.round(prevSum + chargedAmount))
  }
}
